import React from "react";
import {
  Shield,
  Eye,
  Lock,
  Database,
  Mail,
  Calendar,
  Bot,
  Cookie,
  Users,
} from "lucide-react";

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-full p-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Transparency and respect for privacy on the InfrAgents.com
              platform
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Last updated: August 19, 2025
            </p>
          </div>

          {/* Introduction */}
          <div className="mb-12 legal-page-intro-box rounded-xl p-8">
            <div className="flex items-start mb-4">
              <Bot className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold legal-page-section-title mb-3">
                  Privacy Commitment
                </h2>
                <p className="legal-page-text leading-relaxed mb-4">
                  At InfrAgents.com, we value transparency and respect for
                  privacy. This policy explains what information we collect, how
                  we use it, and the limited situations in which it may be
                  shared.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Information We Collect */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Database className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  Information We Collect
                </h2>
              </div>

              <div className="legal-page-text leading-relaxed space-y-4">
                <p>
                  When projects are submitted to the platform, we collect the
                  information provided as part of that submission, such as
                  repository links, descriptions, and tags. If an email address
                  is provided, it is collected only for the purpose of sending
                  updates or notifications, and sharing it is entirely optional.
                </p>
                <p>
                  In addition, we gather basic technical data, such as browser
                  type, device details, and anonymized usage statistics, to help
                  us improve the platform and its performance.
                </p>
              </div>
            </section>

            {/* Information We Do Not Collect */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Eye className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  Information We Do Not Collect
                </h2>
              </div>

              <div className="legal-page-positive-box p-4 rounded-lg">
                <ul className="space-y-2 legal-page-positive-text">
                  <li className="flex items-start">
                    <span className="legal-page-positive-icon mr-2 font-bold">
                      ✓
                    </span>
                    <span>Financial information</span>
                  </li>
                  <li className="flex items-start">
                    <span className="legal-page-positive-icon mr-2 font-bold">
                      ✓
                    </span>
                    <span>
                      Personal data beyond what is voluntarily submitted
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="legal-page-positive-icon mr-2 font-bold">
                      ✓
                    </span>
                    <span>Cross-site tracking or behavioral profiling</span>
                  </li>
                  <li className="flex items-start">
                    <span className="legal-page-positive-icon mr-2 font-bold">
                      ✓
                    </span>
                    <span>Advertising trackers</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* How We Use Information */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Users className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  How We Use Information
                </h2>
              </div>

              <div className="legal-page-text leading-relaxed space-y-4">
                <p>
                  The information we collect is used to display Infrastructure
                  Agents for discovery, to enable search and filtering features,
                  and to ensure the platform functions securely.
                </p>
                <p>
                  When email addresses are provided, they may be used to send
                  notifications or updates if the user has requested this
                  service. Usage data is analyzed in an anonymized form to help
                  us improve discovery features and overall user experience.
                </p>
              </div>
            </section>

            {/* Sharing of Information */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Shield className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  Sharing of Information
                </h2>
              </div>

              <div className="space-y-4">
                <div className="legal-page-notice-box p-4 rounded-lg">
                  <p className="legal-page-notice-text">
                    <strong>Important:</strong> Project submissions are public
                    by design, meaning the information provided about a project
                    will be visible to all platform visitors.
                  </p>
                </div>

                <div className="legal-page-text leading-relaxed space-y-3">
                  <p>Information is shared only in limited circumstances:</p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2 font-bold">•</span>
                      <span>
                        We may work with trusted service providers who assist in
                        operating the platform
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2 font-bold">•</span>
                      <span>
                        In rare cases we may disclose information if required by
                        law or necessary to protect the security and integrity
                        of the platform
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Security */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Lock className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  Data Security
                </h2>
              </div>

              <div className="legal-page-text leading-relaxed">
                <p>
                  We take reasonable measures to protect the information we
                  handle. This includes the use of encryption, access controls,
                  and ongoing monitoring to safeguard against unauthorized
                  access or misuse.
                </p>
              </div>
            </section>

            {/* User Rights */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Users className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  User Rights
                </h2>
              </div>

              <div className="legal-page-text leading-relaxed space-y-4">
                <p>
                  Users have the right to access the data they have submitted,
                  request corrections if the information is inaccurate, or
                  request deletion if they no longer wish their data to be
                  retained.
                </p>
                <p>
                  Users may also withdraw consent for optional communications at
                  any time. Requests can be submitted by contacting us at
                  <a
                    href="mailto:agent@infragents.com"
                    className="text-orange-600 hover:text-orange-700 ml-1"
                  >
                    agent@infragents.com
                  </a>
                  , and we will respond promptly.
                </p>
              </div>
            </section>

            {/* Cookies */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Cookie className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  Cookies
                </h2>
              </div>

              <div className="legal-page-text leading-relaxed space-y-4">
                <p>
                  We use essential cookies to support core platform functions
                  such as authentication and session management. We may also use
                  optional analytics cookies, with user consent, to collect
                  anonymized data that helps us improve performance and user
                  experience.
                </p>
                <p>
                  We do not use advertising cookies or third-party tracking
                  technologies.
                </p>
              </div>
            </section>

            {/* Data Retention */}
            <section className="legal-page-box rounded-xl p-8">
              <div className="flex items-start mb-4">
                <Calendar className="h-6 w-6 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-2xl font-bold legal-page-section-title">
                  Data Retention
                </h2>
              </div>

              <div className="legal-page-text leading-relaxed space-y-3">
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 font-bold">•</span>
                    <span>
                      <strong>Project submissions</strong> remain publicly
                      available for as long as they are listed on the platform
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 font-bold">•</span>
                    <span>
                      <strong>Contact information</strong> is retained only
                      until the user unsubscribes or requests deletion
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 font-bold">•</span>
                    <span>
                      <strong>Anonymized analytics data</strong> is retained for
                      a limited period, after which it is deleted or aggregated
                      for statistical purposes
                    </span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Updates to This Policy */}
            <section className="legal-page-box rounded-xl p-6">
              <div className="flex items-start mb-4">
                <Calendar className="h-5 w-5 text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <h2 className="text-xl font-bold legal-page-section-title">
                  Updates to This Policy
                </h2>
              </div>
              <div className="legal-page-text leading-relaxed space-y-3">
                <p>
                  This Privacy Policy may be updated from time to time to
                  reflect changes in our practices or in legal requirements. Any
                  significant updates will be communicated on the website, and
                  continued use of the platform after changes are posted
                  constitutes acceptance of the revised policy.
                </p>
              </div>
            </section>
          </div>

          {/* Contact Section */}
          <section className="mt-12">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-8 text-white">
              <div className="text-center">
                <Shield className="h-12 w-12 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-4">
                  Privacy Questions or Concerns?
                </h2>
                <p className="text-lg mb-6 text-orange-100 max-w-2xl mx-auto">
                  Transparency and trust are fundamental to our community. We're
                  committed to protecting privacy and are always available to
                  address questions or concerns about data protection.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="mailto:agent@infragents.com"
                    className="inline-flex items-center justify-center bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    agent@infragents.com
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
